import { useNavigate } from 'react-router-dom';

const ProjectManagementPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Back Button */}
      <button
        onClick={() => { navigate('/'); window.scrollTo(0, 0); }}
        data-testid="back-to-home"
        className="fixed top-6 left-6 z-50 flex items-center space-x-2 text-white hover:text-[#00E600] transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span className="font-semibold">Back to Home</span>
      </button>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center px-6 pt-24 pb-16">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxwcm9qZWN0JTIwbWFuYWdlbWVudHxlbnwwfHx8fDE3NjEwNDY3NjR8MA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 uppercase tracking-wide">
            Project Management <span className="text-[#00E600]">SaaS</span>
          </h1>
          <p className="text-xl text-[#B3B3B3] leading-relaxed">
            Chaos kills projects. Precision delivers them. AI-powered project management that predicts delays, optimizes resources, and guarantees execution.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8 uppercase tracking-wide">Overview</h2>
          <div className="space-y-6 text-lg text-[#B3B3B3] leading-relaxed">
            <p className="text-xl text-white font-semibold">
              Your teams work hard. Your projects still fail. Not from lack of effort—but from the invisible chaos 
              between planning and execution. Dependencies no one tracks. Resources stretched too thin. Risks that 
              surface when it's already too late.
            </p>
            <p>
              NYAXO's Project Management SaaS sees what humans can't. Our AI analyzes 10,000+ variables across your 
              portfolio, predicting delays 4-6 weeks in advance, auto-balancing workloads, and surfacing bottlenecks 
              before they become blockers. It's not project tracking—it's project intelligence.
            </p>
            <p>
              The result? $2B+ in enterprise projects delivered on time and under budget. When Fortune 500 companies 
              bet millions on execution, they choose NYAXO. Because in mission-critical work, prediction beats reaction.
            </p>
          </div>
        </div>
      </section>

      {/* AI-Powered Platform */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              AI-Powered <span className="text-[#00E600]">Project Platform</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg max-w-3xl mx-auto">
              Enterprise-grade project orchestration that predicts, prevents, and perfects execution
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Intelligent Planning</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• AI-generated project timelines & milestones</li>
                <li>• Dependency mapping & critical path analysis</li>
                <li>• Automatic schedule optimization</li>
                <li>• Risk-adjusted deadline forecasting</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Smart Resource Allocation</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• ML-based workload balancing</li>
                <li>• Skill-based team matching</li>
                <li>• Capacity planning & forecasting</li>
                <li>• Prevents burnout & overallocation</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Predictive Risk Management</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Early warning system (4-6 weeks ahead)</li>
                <li>• Scope creep detection & prevention</li>
                <li>• Budget overrun predictions</li>
                <li>• Automated mitigation strategies</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Real-Time Analytics</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Live project health dashboards</li>
                <li>• Velocity tracking & burn-down charts</li>
                <li>• Team performance insights</li>
                <li>• Executive reporting automation</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                  <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Collaboration Engine</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• AI-facilitated standups & retrospectives</li>
                <li>• Cross-functional team workspaces</li>
                <li>• Integrated document management</li>
                <li>• Smart notification prioritization</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Workflow Automation</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Automated task creation & assignment</li>
                <li>• Status updates & progress tracking</li>
                <li>• Integration with 50+ tools (Jira, Slack, GitHub)</li>
                <li>• Custom workflow triggers & actions</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Problems We Solve */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Problems We <span className="text-[#00E600]">Solve</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Chronic Delays & Missed Deadlines</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> 70% of projects run late due to poor planning, hidden dependencies, and unrealistic timelines costing millions.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: AI predicts delays 4-6 weeks in advance, auto-adjusts schedules, and surfaces critical path blockers instantly.
              </p>
            </div>

            <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Budget Overruns & Scope Creep</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> Projects balloon by 30-50% due to uncontrolled scope changes and poor cost tracking.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: Real-time budget tracking, scope change impact analysis, and automated approval workflows prevent cost overruns.
              </p>
            </div>

            <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Resource Conflicts & Team Burnout</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> Poor resource allocation leads to 60% team overload while others sit idle, causing turnover and quality issues.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: ML balances workloads across teams, predicts capacity issues, and suggests optimal resource reallocation.
              </p>
            </div>

            <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Communication Chaos</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> Cross-functional teams waste 40% of time in status meetings, Slack threads, and email chains searching for updates.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: AI-generated status reports, smart notifications, and centralized project intelligence eliminate meeting overload.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Industries We <span className="text-[#00E600]">Empower</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Software Development', challenge: 'Agile/DevOps complexity', benefit: '3x faster release cycles, 45% fewer bugs' },
              { name: 'Construction & Engineering', challenge: 'Multi-site coordination', benefit: '94% on-time delivery, 20% under budget' },
              { name: 'Financial Services', challenge: 'Regulatory compliance', benefit: 'Zero audit failures, 50% faster compliance' },
              { name: 'Healthcare', challenge: 'Clinical trial management', benefit: '30% faster trials, FDA-compliant tracking' },
              { name: 'Manufacturing', challenge: 'Product development cycles', benefit: '40% faster time-to-market, reduced defects' },
              { name: 'Marketing & Creative', challenge: 'Campaign execution', benefit: '2x campaign output, real-time ROI tracking' }
            ].map((industry, idx) => (
              <div key={idx} className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
                <div className="w-10 h-10 bg-gradient-to-br from-[#00E600]/20 to-[#36E0A8]/20 rounded-lg flex items-center justify-center mb-4 border border-[#00E600]/30 group-hover:border-[#00E600] transition-all duration-300">
                  <div className="w-2 h-2 bg-[#00E600] rounded-full"></div>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{industry.name}</h3>
                <p className="text-sm text-[#B3B3B3] mb-2"><span className="text-white font-semibold">Challenge:</span> {industry.challenge}</p>
                <p className="text-sm text-[#00E600] font-semibold">→ {industry.benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why NYAXO */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Why <span className="text-[#00E600]">NYAXO</span> Wins
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#0D0D0D] border-l-4 border-[#00E600] rounded-lg p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Competitive Edge</h3>
              <div className="space-y-4 text-[#B3B3B3]">
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">Predictive AI, Not Reactive:</span> 4-6 week early warnings, not post-mortem reports</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">10,000+ Project Data Points:</span> Learns from your history to optimize future projects</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">True Enterprise Scale:</span> Manages $2B+ portfolios with thousands of concurrent projects</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">50+ Integrations:</span> Works with your existing stack (Jira, Slack, GitHub, Salesforce)</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">Expert Consulting:</span> Not just software—strategic PM guidance included</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">On-Time Delivery</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">94%</div>
                <p className="text-[#B3B3B3]">Projects completed on schedule</p>
              </div>
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">Faster Delivery</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">3x</div>
                <p className="text-[#B3B3B3]">Project completion speed increase</p>
              </div>
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">Resource Efficiency</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">45%</div>
                <p className="text-[#B3B3B3]">Reduction in wasted resources</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Software Solutions */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8 uppercase tracking-wide">
            Our <span className="text-[#00E600]">Software Solutions</span>
          </h2>
          <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-8">
            <p className="text-lg text-[#B3B3B3] mb-6">
              NYAXO's project management platform has coordinated $2B+ in projects across industries—from 
              software development to construction, delivering unprecedented execution precision.
            </p>
            <div className="border-l-4 border-[#00E600] pl-6 py-4">
              <p className="text-white font-semibold mb-2">📦 Custom Software Portfolio</p>
              <p className="text-[#B3B3B3] italic">
                [Your specific project management SaaS products will be listed here]
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Elevate Your Project Management</h2>
          <p className="text-lg text-[#B3B3B3] mb-8">
            Stop fighting chaos. Let NYAXO's AI orchestrate your projects.
          </p>
          <button
            onClick={() => navigate('/#contact')}
            className="px-8 py-4 bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D] font-bold rounded-md hover:shadow-[0_0_30px_rgba(0,230,0,0.6)] transition-all duration-300 uppercase tracking-wide"
          >
            Start Free Trial
          </button>
        </div>
      </section>
    </div>
  );
};

export default ProjectManagementPage;